/**
 * RealTestimonials — iter 141
 *
 * Renders honest customer proof on the homepage when (and only when)
 * `realTestimonials` array is non-empty. Empty array → returns null so
 * the homepage stays clean during the founding chapter.
 *
 * Consent policy is enforced by convention, not by code — see the
 * comment block at the top of ``config/realTestimonials.js``.
 */
import React from "react";
import { Quote } from "lucide-react";
import { realTestimonials } from "@/config/realTestimonials";

const initialsOf = (name = "") =>
    name.split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "•";

const RealTestimonials = () => {
    if (!realTestimonials || realTestimonials.length === 0) return null;

    return (
        <section className="section-pad" data-testid="home-real-testimonials">
            <div className="container-page">
                <p className="text-center text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                    In their words
                </p>
                <h2 className="mt-2 text-center font-heading font-semibold text-3xl sm:text-4xl text-foreground">
                    What Simonara customers say
                </h2>

                <div className={`mt-10 grid grid-cols-1 md:grid-cols-${Math.min(realTestimonials.length, 3)} gap-6 max-w-5xl mx-auto`}>
                    {realTestimonials.map((t, i) => (
                        <figure
                            key={`${t.company}-${i}`}
                            className="rounded-2xl border border-border bg-card p-6 flex flex-col"
                            data-testid={`real-testimonial-${i}`}
                        >
                            <Quote size={20} className="text-emerald-600 shrink-0" />
                            <blockquote className="mt-3 text-sm text-foreground leading-relaxed flex-1">
                                &ldquo;{t.quote}&rdquo;
                            </blockquote>
                            <figcaption className="mt-5 flex items-center gap-3">
                                {t.avatar_url ? (
                                    <img
                                        src={t.avatar_url}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                                        {initialsOf(t.name)}
                                    </div>
                                )}
                                <div>
                                    <div className="font-semibold text-sm text-foreground">{t.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {t.role}{t.role && t.company ? " · " : ""}{t.company}
                                    </div>
                                </div>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default RealTestimonials;
